import { ref, computed } from "vue";
import { defineStore } from "pinia";
import type { UserDocument } from "@kekkle/shared/types/documents/user-document/user-document";
import {
  getUserDataFromLocalStorage,
  setUserDataInLocalStorage,
  getActiveGroupIdFromLocalStorage,
  setActiveGroupIdInLocalStorage,
} from "@/helpers/utils/local-storage";
import { fetchUserDoc, updateUserDoc } from "@/helpers/firestore/UserService";
import {
  normalizeSupportedLanguage,
  syncUserPushLanguageForGroups,
} from "@/helpers/firestore/GroupPushSettingsService";
import { getUserProfilePictureFromFirestorage } from "@/helpers/firestorage/UserPictureService";
import {
  clearCachedImages,
  onCacheInvalidated,
} from "@/helpers/cache/image-cache";
import { useAuthStore } from "@/stores/modules/auth/auth";
import { setInfoToast } from "@/helpers/notifications/toast";
import { translate } from "@/i18n/vueI18n";
import { httpsCallable } from "firebase/functions";
import { firebaseFunctions } from "@/helpers/firebase/firebase";
import * as Sentry from "@sentry/vue";
import { FetchStatus } from "@/enums/fetch-status";
import { ImageSizes } from "@/enums/images-sizes";
import { handleFirestorageException } from "@/helpers/exceptions/handler";
import type { StorageError } from "firebase/storage";

type ProfilePictureData = {
  url: string;
  blob?: Blob;
  fromCache: boolean;
};

type UserProfilePictureState = {
  [format: string]: ProfilePictureData;
};

/**
 * User store managing user document data and profile
 * Uses Pinia with Composition API and TypeScript
 */
export const useUserStore = defineStore("user", () => {
  const authStore = useAuthStore();

  /* State */
  const userFetchStatus = ref<FetchStatus>(FetchStatus.Idle);
  const userData = ref<UserDocument | null>(getUserDataFromLocalStorage());
  const userProfilePictureData = ref<UserProfilePictureState>({});
  const rawActiveGroupId = ref<string | null>(
    getActiveGroupIdFromLocalStorage(),
  );

  /* Computed / Getters */
  const activeGroup = computed<string | null>({
    get(): string | null {
      const userGroups = userData.value?.groups ?? [];

      // 1. If stored active group is valid in user's groups, return it
      if (
        rawActiveGroupId.value &&
        userGroups.includes(rawActiveGroupId.value)
      ) {
        return rawActiveGroupId.value;
      }

      // 2. Fall back to userDoc.active_group if valid
      const docActive = userData.value?.active_group;
      if (docActive && userGroups.includes(docActive)) {
        return docActive;
      }

      // 3. Fall back to first available group or null
      return userGroups[0] ?? null;
    },

    set(groupId: string | null) {
      rawActiveGroupId.value = groupId;
      setActiveGroupIdInLocalStorage(groupId);
    },
  });

  const hasUserDoc = computed(() => userData.value !== null);
  const userLoading = computed(
    () => userFetchStatus.value === FetchStatus.Loading,
  );
  const userFailed = computed(
    () => userFetchStatus.value === FetchStatus.Error,
  );

  /* Actions */
  function setUserData(data: UserDocument | null): void {
    userData.value = data;
    setUserDataInLocalStorage(data);
  }

  function setPartialUserData(data: Partial<UserDocument>): void {
    if (userData.value) {
      Object.keys(data).forEach((key) => {
        (userData.value as any)[key] = (data as any)[key];
      });
      setUserDataInLocalStorage(userData.value);
    }
  }

  function setUserProfilePictureData(
    data: ProfilePictureData,
    format: ImageSizes,
  ): void {
    if (!userProfilePictureData.value[format]) {
      userProfilePictureData.value[format] = {
        url: "",
        blob: undefined,
        fromCache: false,
      };
    }
    userProfilePictureData.value[format] = data;
  }

  function reset(): void {
    userFetchStatus.value = FetchStatus.Idle;
    activeGroup.value = null;
    setUserData(null);
    resetUserProfilePictureData();
  }

  function resetUserProfilePictureData(): void {
    userProfilePictureData.value = {};

    // Clear IndexedDB cache for this user
    const userUuid = authStore.authUuid;
    if (userUuid) {
      clearCachedImages(`user:${userUuid}`).catch((error) => {
        console.error("Error clearing user profile picture cache:", error);
      });
    }
  }

  /**
   * Switch the active group locally per app instance
   * Saves selection to localStorage via computed setter and shows toast
   *
   * @param groupId - The group ID to switch to
   */
  async function switchActiveGroup(groupId: string): Promise<void> {
    if (!userData.value || !authStore.authUuid) {
      return;
    }

    if (groupId === activeGroup.value) {
      return;
    }

    activeGroup.value = groupId;

    // Show success toast immediately
    setInfoToast({ message: translate("global.toast.switched_group") });
  }

  async function refreshUser(userUuid: string): Promise<void> {
    try {
      userFetchStatus.value = FetchStatus.Loading;
      const userDoc: UserDocument | null = await fetchUserDoc(userUuid);
      if (userDoc) {
        setUserData(userDoc);

        // Check if auth claims need to be refreshed
        if (userDoc?.new_auth_claim === true) {
          const authUser = authStore.firebaseAuthUser;
          if (authUser) {
            await authStore.refreshAuthIdToken();
            await updateUserDoc(userUuid, { new_auth_claim: false });
          }
        }

        // Check if the auth claim and the number of groups is correct
        const authTokenGroupCount = Object.values(
          authStore.authIdTokenClaims || {},
        ).filter((value) =>
          ["admin", "organiser", "member"].includes(value as string),
        ).length;

        if (userDoc.groups && userDoc.groups.length !== authTokenGroupCount) {
          Sentry.captureMessage(
            `Auth claim for user ${userDoc._id} is not correct, calling method to correct this`,
          );
          const fixPermissions = httpsCallable(
            firebaseFunctions,
            "fixPermissions",
          );
          await fixPermissions();
          // Locally set that the auth claim is changed
          if (userData.value) {
            userData.value.new_auth_claim = true;
          }
        }
        userFetchStatus.value = FetchStatus.Success;
      } else {
        // User document doesn't exist - valid for new users who need to complete profile
        setUserData(null);
        userFetchStatus.value = FetchStatus.Success;
        console.log(
          `No user document found for userUuid: ${userUuid} - user may need to complete profile`,
        );
      }
    } catch (error) {
      // This is a real error (network, permissions, etc.) - not just missing doc
      console.error("Error refreshing user:", error);
      Sentry.captureException(error);
      userFetchStatus.value = FetchStatus.Error;
      setUserData(null);
      throw error; // Re-throw so app initialization can handle it
    }
  }

  async function postUserLanguage(language: string): Promise<void> {
    if (!userData.value || !authStore.authUuid) {
      return;
    }
    const normalizedLanguage = normalizeSupportedLanguage(language);
    try {
      await updateUserDoc(authStore.authUuid, { language: normalizedLanguage });
      setPartialUserData({ language: normalizedLanguage });
      await syncUserPushLanguageForGroups({
        userId: authStore.authUuid,
        groupIds: userData.value.groups || [],
        language: normalizedLanguage,
      });
    } catch (error) {
      console.error("Error updating user language:", error);
    }
  }

  async function downloadUserProfilePictureData(
    format: ImageSizes = ImageSizes.SD,
  ): Promise<void> {
    if (!authStore.authUuid) return;

    try {
      const result = await getUserProfilePictureFromFirestorage(
        authStore.authUuid,
        format,
      );
      const userProfilePictureData: ProfilePictureData = {
        blob: result.blob,
        url: result.url,
        fromCache: result.fromCache,
      };
      setUserProfilePictureData(userProfilePictureData, format);
    } catch (error) {
      handleFirestorageException({ e: error as StorageError });
    }
  }

  /**
   * Refresh current user data with auth claim check
   * Simple wrapper around refreshUser using current auth UUID
   * Redirects to GroupSelector if no active group after refresh
   */
  async function refreshUserData(): Promise<void> {
    const userUuid: string | undefined = authStore.authUuid;
    if (!userUuid) {
      return;
    }
    await refreshUser(userUuid);

    // After refresh, check if active group is missing
    const currentActiveGroup = activeGroup.value;

    // If no active group, redirect to GroupSelector
    if (!currentActiveGroup) {
      const router = await import("@/router/router");
      const currentRoute = router.default.currentRoute.value;

      // Only redirect if we're on a page that requires a group
      if (currentRoute.meta?.requiresGroup) {
        await router.default.push({ name: "GroupSelector" });
      }
    }
  }

  /* Register cache invalidation callback */
  onCacheInvalidated((keyPattern) => {
    // Pattern format: "user:userUuid"
    if (keyPattern.startsWith("user:")) {
      const parts = keyPattern.split(":");
      const userUuid = parts[1];

      // Check if this is the current user
      if (userUuid && userUuid === authStore.authUuid) {
        // Get the existing formats before clearing
        const existingFormats = Object.keys(userProfilePictureData.value);

        // Clear store data
        userProfilePictureData.value = {};

        // Refetch only the sizes that were previously loaded
        existingFormats.forEach((format) => {
          downloadUserProfilePictureData(format as ImageSizes);
        });
      }
    }
  });

  /* Exposed store */
  return {
    // State
    userLoading,
    userFailed,
    userData,
    userProfilePictureData,

    // Computed / State
    activeGroup,
    hasUserDoc,

    // Actions
    setUserData,
    setPartialUserData,
    setUserProfilePictureData,
    reset,
    resetUserProfilePictureData,
    switchActiveGroup,
    refreshUser,
    refreshUserData,
    postUserLanguage,
    downloadUserProfilePictureData,
  };
});
